import React, { useState, useRef, useEffect } from 'react'
import {
  FaBell,
  FaShieldAlt,
  FaFileInvoiceDollar,
  FaTools,
  FaBuilding,
  FaTimes,
  FaCheckDouble,
  FaClock,
  FaChevronRight,
  FaTrashAlt,
  FaInbox,
  FaInfoCircle
} from 'react-icons/fa'
import { usePG } from '../../context/PGManagementContext'
import { useAuth } from '../../context/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

// Helper: Format relative timestamp
const timeAgo = (dateInput) => {
  if (!dateInput) return 'Just now'
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) return 'Just now'
  const seconds = Math.floor((new Date() - date) / 1000)
  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// Helper: Notification metadata & high-contrast styling
const getNotificationMeta = (type) => {
  switch (type?.toUpperCase()) {
    case 'APPLICATION':
    case 'KYC':
      return {
        icon: FaShieldAlt,
        iconBg: 'bg-purple-100 text-purple-700 border-purple-300',
        badge: 'KYC & Booking',
        badgeStyle: 'bg-purple-100 text-purple-900 border-purple-200'
      }
    case 'INVOICE':
    case 'PAYMENT':
      return {
        icon: FaFileInvoiceDollar,
        iconBg: 'bg-emerald-100 text-emerald-700 border-emerald-300',
        badge: 'Billing & Rent',
        badgeStyle: 'bg-emerald-100 text-emerald-900 border-emerald-200'
      }
    case 'COMPLAINT':
    case 'MAINTENANCE':
      return {
        icon: FaTools,
        iconBg: 'bg-amber-100 text-amber-700 border-amber-300',
        badge: 'Support Desk',
        badgeStyle: 'bg-amber-100 text-amber-900 border-amber-200'
      }
    case 'ANNOUNCEMENT':
    case 'SOCIETY':
      return {
        icon: FaBuilding,
        iconBg: 'bg-blue-100 text-blue-700 border-blue-300',
        badge: 'Announcement',
        badgeStyle: 'bg-blue-100 text-blue-900 border-blue-200'
      }
    default:
      return {
        icon: FaInfoCircle,
        iconBg: 'bg-indigo-100 text-indigo-700 border-indigo-300',
        badge: 'Update',
        badgeStyle: 'bg-indigo-100 text-indigo-900 border-indigo-200'
      }
  }
}

const NotificationBellPopup = ({ targetRole, align = 'right' }) => {
  const { notifications, deleteNotification, clearNotifications } = usePG()
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState('ALL') // 'ALL' | 'UNREAD'
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ch24_read_notifs') || '[]')
    } catch {
      return []
    }
  })

  const popupRef = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Security guard: If user is not logged in, do not render notifications
  if (!isAuthenticated || !user) {
    return null
  }

  // Determine effective role
  const effectiveRole = targetRole || (user?.role === 'admin' ? 'ADMIN' : 'RESIDENT')

  // Filter notifications strictly relevant to current authenticated viewer
  const roleFiltered = (notifications || []).filter((n) => {
    if (effectiveRole === 'ADMIN') {
      return n.target === 'ADMIN' || n.target === 'STAFF' || n.target === 'ALL'
    }
    if (effectiveRole === 'RESIDENT') {
      return n.target === 'RESIDENT' || n.target === 'ALL'
    }
    return false
  })

  const unreadCount = roleFiltered.filter((n) => !readIds.includes(n.id)).length

  const displayList = roleFiltered.filter((n) => {
    if (filter === 'UNREAD') return !readIds.includes(n.id)
    return true
  })


  const markAllAsRead = () => {
    const allIds = roleFiltered.map((n) => n.id)
    setReadIds(allIds)
    try {
      localStorage.setItem('ch24_read_notifs', JSON.stringify(allIds))
    } catch (e) {
      console.warn(e)
    }
    toast.success('All notifications marked as read')
  }

  const markSingleAsRead = (id, link) => {
    if (!readIds.includes(id)) {
      const updated = [...readIds, id]
      setReadIds(updated)
      try {
        localStorage.setItem('ch24_read_notifs', JSON.stringify(updated))
      } catch (e) {
        console.warn(e)
      }
    }
    if (link) {
      navigate(link)
      setIsOpen(false)
    }
  }

  const handleDeleteOne = (e, id) => {
    e.stopPropagation() // Prevent triggering row click/navigation
    if (typeof deleteNotification === 'function') {
      deleteNotification(id)
    }
    // Clean from readIds cache
    const updated = readIds.filter((item) => item !== id)
    setReadIds(updated)
    try {
      localStorage.setItem('ch24_read_notifs', JSON.stringify(updated))
    } catch (err) {
      console.warn(err)
    }
    toast.success('Notification entry deleted')
  }

  const handleClearAll = () => {
    if (typeof clearNotifications === 'function') {
      clearNotifications(effectiveRole)
    }
    setReadIds([])
    try {
      localStorage.removeItem('ch24_read_notifs')
    } catch (err) {
      console.warn(err)
    }
    toast.success('All notifications cleared')
  }

  // Alignment classes
  const alignmentClass =
    align === 'left'
      ? 'left-0'
      : align === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : 'right-0'

  return (
    <div className="relative inline-block text-left" ref={popupRef}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="View notifications"
        className={`relative p-2.5 rounded-2xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 border shadow-xs group cursor-pointer flex items-center justify-center ${
          isOpen
            ? 'bg-primary-50 border-primary-300 text-primary-700 ring-2 ring-primary-200'
            : 'bg-white hover:bg-purple-50/70 text-slate-700 hover:text-primary-700 border-slate-200'
        }`}
      >
        <FaBell
          size={16}
          className={`transition-transform duration-200 ${
            isOpen
              ? 'text-primary-600 scale-110'
              : 'text-slate-700 group-hover:text-primary-600 group-hover:rotate-12'
          }`}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-rose-600 px-1 text-[10px] font-black text-white shadow-md border-2 border-white animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Solid Opaque Notification Popup */}
      {isOpen && (
        <div
          style={{ backgroundColor: '#ffffff', opacity: 1, zIndex: 99999 }}
          className={`absolute ${alignmentClass} mt-2 w-[360px] sm:w-[420px] max-w-[calc(100vw-1.5rem)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.28)] border border-slate-200 overflow-hidden animate-fade-in text-slate-800`}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800 gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-purple-600/30 border border-purple-400/40 flex items-center justify-center text-amber-300 shadow-inner shrink-0">
                <FaBell size={14} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-bold tracking-tight font-heading text-white whitespace-nowrap">
                    Notifications
                  </h4>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white uppercase tracking-wider whitespace-nowrap shrink-0 shadow-xs">
                      {unreadCount} NEW
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-300 font-medium whitespace-nowrap truncate">
                  {unreadCount > 0
                    ? `${unreadCount} unread update${unreadCount > 1 ? 's' : ''}`
                    : 'All caught up'}
                </p>
              </div>
            </div>

            {/* Header Action Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="text-[11px] font-bold bg-white/15 hover:bg-white/25 text-white px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-white/20 shadow-xs active:scale-95 whitespace-nowrap shrink-0"
                  title="Mark all as read"
                >
                  <FaCheckDouble size={11} className="shrink-0" />
                  <span className="whitespace-nowrap">Read All</span>
                </button>
              )}

              {roleFiltered.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-[11px] font-bold bg-rose-500/20 hover:bg-rose-500/35 text-rose-200 hover:text-white px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-rose-400/30 shadow-xs active:scale-95 whitespace-nowrap shrink-0"
                  title="Clear all notifications"
                >
                  <FaTrashAlt size={11} className="shrink-0" />
                  <span className="whitespace-nowrap">Clear All</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-slate-300 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer shrink-0"
                title="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          {/* Filter Pills Tabs */}
          <div className="flex items-center justify-between px-3.5 py-2 bg-slate-100/90 border-b border-slate-200">
            <div className="flex items-center gap-1.5 bg-slate-200/80 p-0.5 rounded-xl">
              <button
                type="button"
                onClick={() => setFilter('ALL')}
                className={`px-3 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer whitespace-nowrap ${
                  filter === 'ALL'
                    ? 'bg-white text-primary-800 shadow-xs font-extrabold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({roleFiltered.length})
              </button>
              <button
                type="button"
                onClick={() => setFilter('UNREAD')}
                className={`px-3 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                  filter === 'UNREAD'
                    ? 'bg-white text-primary-800 shadow-xs font-extrabold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Unread</span>
                {unreadCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center shrink-0">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>

            <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
              {displayList.length} shown
            </span>
          </div>

          {/* Notification Items List */}
          <div
            style={{ backgroundColor: '#ffffff' }}
            className="max-h-[380px] overflow-y-auto divide-y divide-slate-100 custom-scrollbar"
          >
            {displayList.length === 0 ? (
              <div
                style={{ backgroundColor: '#ffffff' }}
                className="p-8 text-center flex flex-col items-center justify-center space-y-2.5"
              >
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                  <FaInbox size={22} className="text-slate-400" />
                </div>
                <p className="text-sm font-bold text-slate-800">No Notifications</p>
                <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                  {filter === 'UNREAD'
                    ? "You've caught up with all unread notifications."
                    : 'No notification records present at this time.'}
                </p>
              </div>
            ) : (
              displayList.map((notif) => {
                const meta = getNotificationMeta(notif.type)
                const IconComponent = meta.icon
                const isUnread = !readIds.includes(notif.id)

                return (
                  <div
                    key={notif.id}
                    onClick={() => markSingleAsRead(notif.id, notif.link)}
                    style={{
                      backgroundColor: isUnread ? '#f8f5ff' : '#ffffff'
                    }}
                    className={`p-3.5 transition-all cursor-pointer flex items-start gap-3 relative group border-l-4 ${
                      isUnread
                        ? 'border-l-primary-600 hover:bg-[#efeaff]'
                        : 'border-l-transparent hover:bg-slate-50'
                    }`}
                  >
                    {/* Icon Badge */}
                    <div
                      className={`p-2.5 rounded-xl border text-sm shrink-0 shadow-xs mt-0.5 ${meta.iconBg}`}
                    >
                      <IconComponent size={15} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span
                          className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-md border shadow-2xs whitespace-nowrap shrink-0 ${meta.badgeStyle}`}
                        >
                          {meta.badge}
                        </span>
                        <span className="text-[11px] text-slate-500 font-semibold flex items-center gap-1 whitespace-nowrap">
                          <FaClock size={10} className="text-slate-400 shrink-0" />
                          {timeAgo(notif.timestamp || notif.createdAt)}
                        </span>
                        {isUnread && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black bg-rose-500 text-white uppercase tracking-wider whitespace-nowrap shrink-0">
                            NEW
                          </span>
                        )}
                      </div>

                      <h5 className="text-xs sm:text-sm font-extrabold text-slate-900 leading-snug">
                        {notif.title}
                      </h5>

                      <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed mt-1 font-medium">
                        {notif.message}
                      </p>

                      {notif.link && (
                        <div className="mt-2 flex items-center gap-1 text-[11px] font-bold text-primary-700 group-hover:text-primary-900">
                          <span>View Details</span>
                          <FaChevronRight
                            size={9}
                            className="group-hover:translate-x-0.5 transition-transform"
                          />
                        </div>
                      )}
                    </div>

                    {/* Individual Delete / Dismiss Button */}
                    <div className="shrink-0 flex items-center self-center pl-1">
                      <button
                        type="button"
                        onClick={(e) => handleDeleteOne(e, notif.id)}
                        title="Delete this notification entry"
                        aria-label="Delete entry"
                        className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors border border-transparent hover:border-red-200 cursor-pointer shadow-none hover:shadow-xs active:scale-95"
                      >
                        <FaTrashAlt size={13} />
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
            <span className="text-[11px] font-semibold text-slate-500 truncate">
              Chapter24 Working Women's Residence
            </span>
            <Link
              to={user?.role === 'admin' ? '/admin' : '/portal'}
              onClick={() => setIsOpen(false)}
              className="text-xs font-bold text-primary-700 hover:text-primary-900 hover:underline flex items-center gap-1 shrink-0 ml-2"
            >
              <span>{user?.role === 'admin' ? 'Admin Console' : 'Resident Portal'}</span>
              <FaChevronRight size={10} />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBellPopup
